import classNames from "classnames";
import dayjs from "dayjs";
import parse from "html-react-parser";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "swiper/css";
import { Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IProductDetailQuery } from "@repo/shopify/types/api/product";
import type { Collection, ProductEdge } from "@repo/shopify/types/generated/storefront.types";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { ImageContent, TextContent } from "@molecules/ParseContent";
import ProductCard from "@molecules/ProductCard";

import FeaturesCard from "@organisms/FeaturesCard";

import { URLS } from "@const/urls";

import useFeatures from "@hooks/useFeatures";
import useFeaturesDetail from "@hooks/useFeaturesDetail";

import { useLayoutStore } from "@store/layout";

interface IProps {
  type: "news" | "lookbook";
}

interface IArticleImage {
  src: string;
  alt: string;
  width: string | number;
  height: string | number;
}

export default function FeaturesDetail({ type }: IProps) {
  const navigate = useNavigate();
  const { handle } = useParams();

  const { isPC } = useCheckDevice();

  const { showFooter } = useLayoutStore();

  const { loading, article, prevArticle, nextArticle } = useFeaturesDetail(type, `blog_title:${type === "lookbook" ? "Lookbook" : "News"}`);

  const [enlargedIndex, setEnlargedIndex] = useState(0);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [articleImages, setArticleImages] = useState<IArticleImage[]>([]);
  const [hoverZone, setHoverZone] = useState<"left" | "right" | null>(null);

  useEffect(() => {
    showFooter(true);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  // article contentHtml에서 img 태그 정보를 파싱하여 저장
  useEffect(() => {
    if (article?.contentHtml) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = article.contentHtml;
      const imgs = Array.from(tempDiv.querySelectorAll("img")).map((img) => ({
        src: img.src,
        alt: img.alt,
        width: img.getAttribute("width") || 1280,
        height: img.getAttribute("height") || 1707,
      }));
      setArticleImages(imgs);
    }
  }, [article?.contentHtml]);

  const relatedProducts = useMemo(() => (article?.relatedProducts?.references?.edges as ProductEdge[])?.slice(0, 4) || [], [article]);

  const { blogs } = useFeatures();

  const mostRecent = useMemo(() => {
    if (type === "lookbook") return [];

    return (
      blogs?.edges
        .flatMap(({ node }) => node)
        .map((item) => item.articles)
        .flatMap((item) => item?.edges ?? [])
        .sort((prev, curr) => new Date(curr.node.publishedAt).getTime() - new Date(prev.node.publishedAt).getTime())
        ?.slice(0, 3) || []
    );
  }, [blogs, type]);

  // 이미지 클릭 시 확대 뷰 띄우기
  const handleContentClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName.toLowerCase() === "img") {
      const imgElement = target as HTMLImageElement;
      const idx = articleImages.findIndex((img) => img.src === imgElement.src);
      if (idx !== -1) {
        setEnlargedIndex(idx);

        setIsEnlarged(true);
      }
    }
  };

  const closeEnlarged = () => {
    setIsEnlarged(false);
    setHoverZone(null); // 닫을 때 호버 상태 초기화
  };

  return (
    <>
      {loading ? (
        <Loading type="page" />
      ) : (
        <>
          <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x pc:grid pc:gap-y-60 pc:flex-row col-span-8 flex flex-col gap-y-20">
            {type === "news" ? (
              <>
                {isPC ? (
                  <section className="pc:col-span-12 tnt-gap-x pc:grid-cols-12 grid">
                    {/* 텍스트가 sticky 로 따라붙으려면 이미지가 같은 컨테이너 안에서 높이를 만들어야 한다 */}
                    <section className="pc:col-span-12 tnt-gap-x relative flex h-fit">
                      <div className="pc:top-30 pc:sticky tnt-gap-x flex h-fit">
                        <div className="pc:w-screen-col-span-2 flex h-fit items-center justify-start gap-1.5">
                          <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                            {type}
                          </Link>
                          <Icon className="h-3" name="RightArrow" />
                          <Link className="capitalize" href={URLS.FEATURES}>
                            All
                          </Link>
                        </div>

                        <div className="pc:w-screen-col-span-5 flex h-fit flex-col gap-10">
                          <div className="flex flex-col gap-1.5 leading-none">
                            <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                            {article?.collection?.reference ? (
                              <Link
                                className="text-title"
                                href={`${URLS.COLLECTIONS_DETAIL}/${(article?.collection?.reference as Collection)?.handle}`}
                              >
                                {article?.title || ""}
                              </Link>
                            ) : (
                              <h1 className="text-title">{article?.title || ""}</h1>
                            )}
                          </div>
                          <TextContent html={article?.contentHtml || ""} />
                        </div>
                      </div>
                      {/* flex 컨테이너 안이라 col-span-* 은 적용되지 않는다. 남은 5칼럼을 flex-1 로 채운다 */}
                      <Button as="wrapper" className="flex min-w-0 flex-1 flex-col gap-5" onClick={handleContentClick}>
                        <ImageContent html={article?.contentHtml || ""} />
                      </Button>
                    </section>
                  </section>
                ) : (
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                      <div className="pc:w-screen-col-span-2 flex h-fit items-center justify-start gap-5 leading-none">
                        <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                          {type}
                        </Link>
                        <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                      </div>

                      <h1 className="text-title">{article?.title || ""}</h1>
                    </div>
                    <TextContent html={article?.contentHtml || ""} />

                    <Button as="wrapper" className="pc:col-span-5 pc:gap-5 flex flex-col gap-4" onClick={handleContentClick}>
                      <ImageContent html={article?.contentHtml || ""} />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {isPC ? (
                  <section className="pc:col-span-12 tnt-gap-x pc:grid-cols-12 relative grid grid-cols-8">
                    <div className="pc:col-span-2 pc:top-30 pc:sticky flex h-fit items-center justify-start gap-1.5">
                      <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                        {type}
                      </Link>
                      <Icon className="h-3" name="RightArrow" />
                      <Link className="capitalize" href={`${URLS.LOOKBOOK}?year=${article?.tags?.[0] || "all"}`}>
                        {article?.tags?.[0] || "All"}
                      </Link>
                    </div>

                    <div className="pc:col-span-10 flex h-fit flex-col gap-2.5">
                      <div className="flex flex-col gap-1.5 leading-none">
                        <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                        <h1 className="text-title">{article?.title || ""}</h1>
                      </div>
                      <TextContent html={article?.contentHtml || ""} />

                      <Button
                        as="wrapper"
                        className="pc:col-span-10 pc:grid pc:grid-cols-2 flex h-fit flex-col gap-5"
                        onClick={handleContentClick}
                      >
                        <ImageContent html={article?.contentHtml || ""} />
                      </Button>
                    </div>
                  </section>
                ) : (
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                      <div className="pc:w-screen-col-span-2 flex h-fit items-center justify-start gap-1.5 leading-none">
                        <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                          {type}
                        </Link>
                        <Icon className="h-2.5" name="RightArrow" />
                        <Link className="capitalize" href={`${URLS.LOOKBOOK}?year=${article?.tags?.[0] || ""}`}>
                          {article?.tags?.[0] || "All"}
                        </Link>
                      </div>
                      <h1 className="text-title">{article?.title || ""}</h1>
                    </div>
                    <TextContent html={article?.contentHtml || ""} />

                    <Button as="wrapper" className="pc:col-span-5 pc:gap-5 flex flex-col gap-4" onClick={handleContentClick}>
                      <ImageContent html={article?.contentHtml || ""} />
                    </Button>
                  </div>
                )}
              </>
            )}

            {relatedProducts.length > 0 ? (
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x grid grid-cols-2 gap-2">
                <div className="pc:justify-start col-span-2 flex h-fit justify-between gap-2">
                  <span>Related Products</span>
                  <Button
                    isActive={false}
                    onClick={() => {
                      navigate(`${URLS.COLLECTIONS_DETAIL}/${(article?.collection?.reference as Collection)?.handle || "all"}`);
                    }}
                  >
                    View All
                  </Button>
                </div>

                <div className="pc:col-span-10 pc:grid-cols-5 col-span-2 grid grid-cols-2 gap-5">
                  {relatedProducts.map(({ node }) => (
                    <ProductCard
                      color={node?.options.find((item) => item.name === "Color")?.values || []}
                      key={node.id}
                      releaseDate={(node as IProductDetailQuery["product"])?.release_date}
                      title={node.title}
                      variants={node.variants}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {type === "lookbook" && (prevArticle || nextArticle) ? (
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x grid grid-cols-1 gap-5">
                <div className="pc:justify-start pc:col-span-2 col-span-1 flex h-fit justify-between gap-2">
                  <span>Explore More</span>
                  <Button
                    isActive={false}
                    onClick={() => {
                      navigate(URLS.LOOKBOOK);
                    }}
                  >
                    View All
                  </Button>
                </div>
                <div className="pc:col-span-10 pc:grid-cols-2 pc:gap-5 col-span-1 grid grid-cols-1 items-end gap-10">
                  {prevArticle ? (
                    <Link className="flex! w-full flex-col" href={`/features/lookbook/${prevArticle.handle}`}>
                      <div className="flex! w-full flex-col gap-2">
                        <div className="pc:col-span-2 col-span-1 flex flex-col gap-0.5 font-normal">
                          <span className="font-bold">Prev</span>
                          <span className="font-bold">{parse(prevArticle.title || "")}</span>
                          {prevArticle.excerptHtml ? <>{parse(prevArticle.excerptHtml || "")}</> : null}
                        </div>

                        <div className="relative aspect-4/5 w-full overflow-hidden bg-gray-100">
                          <Image
                            alt={prevArticle.image?.altText || ""}
                            className="h-full w-full object-cover"
                            height={prevArticle.image?.height || 500}
                            src={prevArticle.image?.url || ""}
                            width={prevArticle.image?.width || 400}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : null}

                  {nextArticle ? (
                    <Link className="flex! w-full flex-col" href={`/features/lookbook/${nextArticle.handle}`}>
                      <div className="flex! w-full flex-col gap-2">
                        <div className="pc:col-span-2 col-span-1 flex flex-col gap-0.5 font-normal">
                          <span className="font-bold">Next</span>
                          <span className="font-bold">{parse(nextArticle.title || "")}</span>
                          {nextArticle.excerptHtml ? <>{parse(nextArticle.excerptHtml || "")}</> : null}
                        </div>

                        <div className="relative aspect-4/5 w-full overflow-hidden bg-gray-100">
                          <Image
                            alt={nextArticle.image?.altText || ""}
                            className="h-full w-full object-cover"
                            height={nextArticle.image?.height || 500}
                            src={nextArticle.image?.url || ""}
                            width={nextArticle.image?.width || 400}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : null}
                </div>
              </section>
            ) : null}

            {mostRecent.length > 0 && (
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x grid grid-cols-1 gap-2">
                <div className="pc:justify-start pc:col-span-2 col-span-1 flex h-fit justify-between gap-2">
                  <span>Most Recent</span>
                  <Button
                    isActive={false}
                    onClick={() => {
                      navigate(`${URLS.FEATURES}/${type}`);
                    }}
                  >
                    View All
                  </Button>
                </div>
                <div className="pc:col-span-10 pc:grid-cols-3 grid-span-1 pc:gap-5 grid grid-cols-1 gap-10">
                  {mostRecent.map(({ node }) => (
                    <FeaturesCard category="all" key={node.id} node={node} />
                  ))}
                </div>
              </section>
            )}

            <Button
              as="text"
              className="pc:col-span-12 col-span-8 my-30 flex place-self-center"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Back to Top
            </Button>
          </section>

          {isEnlarged ? (
            <>
              {/* 마우스 포인터 감지를 위한 Wrapper. (모바일/PC 공통 기능) */}
              <div
                className="fixed inset-0 z-70 grid grid-cols-3 bg-white"
                onPointerLeave={() => {
                  setHoverZone(null);
                }}
                onPointerMove={(e) => {
                  if (!isPC) return;

                  // 상단 헤더영역(대략 60px)에 위치할 시에는 HoverText 노출하지 않음
                  if (e.clientY < 60) {
                    setHoverZone(null);
                    return;
                  }

                  const x = e.clientX;
                  const width = window.innerWidth;

                  // 화면을 3등분하여 좌표가 위치한 곳 상태 기록
                  if (x < width / 3) setHoverZone("left");
                  else if (x > (width * 2) / 3) setHoverZone("right");
                  else setHoverZone(null);
                }}
              >
                <Swiper
                  className={classNames(
                    "col-span-3 size-full",
                    (hoverZone === "left" && enlargedIndex > 0) || (hoverZone === "right" && enlargedIndex < articleImages.length - 1)
                      ? "cursor-pointer"
                      : "cursor-default",
                  )}
                  initialSlide={enlargedIndex}
                  modules={[Mousewheel]}
                  mousewheel={{ forceToAxis: false }}
                  onClick={(swiper, e) => {
                    const event = e as MouseEvent | TouchEvent;
                    const x = "clientX" in event ? event.clientX : (event as unknown as TouchEvent).changedTouches?.[0]?.clientX;

                    if (x === undefined) return;

                    const width = window.innerWidth;

                    if (x < width / 3) {
                      if (swiper.activeIndex > 0) swiper.slidePrev();
                    }

                    if (x > (width * 2) / 3) {
                      if (swiper.activeIndex < articleImages.length - 1) swiper.slideNext();
                    }
                  }}
                  onSlideChange={(swiper) => {
                    setEnlargedIndex(swiper.activeIndex);
                  }}
                  slidesPerView={1}
                  spaceBetween={0}
                  speed={600}
                >
                  {articleImages.map((img, index) => (
                    <SwiperSlide key={index}>
                      <Image
                        alt={img.alt || ""}
                        className="h-dvh w-full bg-white! px-4 py-4 [&>img]:object-contain!"
                        height={Number(img.height) || 1707}
                        loading="eager"
                        src={img.src || ""}
                        width={Number(img.width) || 1280}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              <div className="pc:px-5 pc:h-12.5 pc:min-h-12.5 fixed top-0 left-0 z-80 mx-auto flex h-13.5 min-h-13.5 w-full items-end justify-between p-4 text-black">
                <Link className="text-title font-features-normal flex w-fit leading-none" href={URLS.HOME}>
                  thisisneverthat®
                </Link>
                <Button as="text" onClick={closeEnlarged}>
                  {isPC ? "Close" : <Icon className="size-4.5" name="Close" />}
                </Button>
              </div>

              {isPC ? (
                <>
                  <span
                    className={classNames(
                      "pointer-events-none fixed top-1/2 left-5 z-70 text-black",
                      hoverZone === "left" && enlargedIndex > 0 ? "opacity-100" : "opacity-0",
                    )}
                  >
                    Previous
                  </span>
                  <span
                    className={classNames(
                      "pointer-events-none fixed top-1/2 right-5 z-70 text-black",
                      hoverZone === "right" && enlargedIndex < articleImages.length - 1 ? "opacity-100" : "opacity-0",
                    )}
                  >
                    Next
                  </span>
                </>
              ) : null}

              <div className={classNames("fixed bottom-4 z-70 text-black", isPC ? "right-5" : "bottom-4 left-4")}>
                {enlargedIndex + 1} of {articleImages.length}
              </div>
            </>
          ) : null}
        </>
      )}
    </>
  );
}
